import type { GlobalFieldId, GlobalPrimitiveFieldId, GlobalSingleRelationFieldId } from '#layers/data-model/types/schema/ids';
import type {
	ResolvedMonospaceCollection,
	ResolvedMonospacePrimitiveField,
	ResolvedMonospaceSingleRelationField,
} from '#layers/data-model/types/schema/resolved-schema';
import type { CollectionFieldPath } from '#layers/data-model/utils/collection-field-path';
import { fieldIdToCollectionFieldPath } from '#layers/data-model/utils/collection-field-path';

export function isFieldHidden(field: ResolvedMonospacePrimitiveField | ResolvedMonospaceSingleRelationField) {
	return !!field.meta?.isHidden;
}

type FindCollectionFieldByIdReturn<TId extends GlobalFieldId> = TId extends GlobalPrimitiveFieldId ? ResolvedMonospacePrimitiveField
	: TId extends GlobalSingleRelationFieldId ? ResolvedMonospaceSingleRelationField : ResolvedMonospacePrimitiveField | ResolvedMonospaceSingleRelationField;

export function findCollectionFieldById(collection: ResolvedMonospaceCollection, fieldId: GlobalPrimitiveFieldId): ResolvedMonospacePrimitiveField | undefined;
export function findCollectionFieldById(collection: ResolvedMonospaceCollection, fieldId: GlobalSingleRelationFieldId): ResolvedMonospaceSingleRelationField | undefined;
export function findCollectionFieldById(collection: ResolvedMonospaceCollection, fieldId: GlobalFieldId): ResolvedMonospacePrimitiveField | ResolvedMonospaceSingleRelationField | undefined;

export function findCollectionFieldById<TId extends GlobalFieldId>(collection: ResolvedMonospaceCollection, fieldId: TId): FindCollectionFieldByIdReturn<TId> | undefined {
	return (collection.primitiveFields.find((field) => field.id === fieldId) ?? collection.relationFields.find((field) => field.id === fieldId)) as FindCollectionFieldByIdReturn<TId> | undefined;
}

export function findCollectionPrimitiveFieldByApiName(collection: ResolvedMonospaceCollection, apiName: string): ResolvedMonospacePrimitiveField | undefined {
	return collection.primitiveFields.find((field) => field.apiName === apiName);
}

export function findCollectionRelationFieldByApiName(collection: ResolvedMonospaceCollection, apiName: string): ResolvedMonospaceSingleRelationField | undefined {
	return collection.relationFields.find((field) => field.apiName === apiName);
}

export function findCollectionFieldByApiName(collection: ResolvedMonospaceCollection, apiName: string): ResolvedMonospacePrimitiveField | ResolvedMonospaceSingleRelationField | undefined {
	return findCollectionPrimitiveFieldByApiName(collection, apiName) ?? findCollectionRelationFieldByApiName(collection, apiName);
}

export function getFieldIds(collection: ResolvedMonospaceCollection, apiNames: string[]): GlobalFieldId[] {
	return apiNames
		.map((apiName) => findCollectionFieldByApiName(collection, apiName)?.id)
		.filter((id) => id !== undefined);
}

/**
 * Resolve api names to the one-segment field paths used by column configuration.
 * Api names without a matching field are skipped.
 */
export function getFieldPaths(collection: ResolvedMonospaceCollection, apiNames: string[]): CollectionFieldPath[] {
	return getFieldIds(collection, apiNames).map((fieldId) => fieldIdToCollectionFieldPath(fieldId));
}
